"use client";

import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  BellRing,
  ChevronRight,
  LayoutDashboard,
  MapPin,
  Menu,
  PlugZap,
  ScanLine,
  TrendingUp,
  X,
  type LucideIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { BrandGlobe } from "@/components/landing/BrandGlobe";
import { Timeline } from "@/components/landing/Timeline";
import {
  BrandList,
  CookieSettingsLink,
  FaqAccordion,
  Flag,
  LanguageSwitcher,
  PersonaToggle,
  ScrollRevealText,
  VideoBentoCard,
  renderAccented,
  type FaqItem,
} from "@/components/landing/sections";
import { WelcomeDialog, type Persona } from "@/components/landing/WelcomeDialog";
import { APP_LOGIN_URL, APP_TRIAL_URL, BOOK_DEMO_URL } from "@/lib/links";
import { localePath, type Locale } from "@/lib/i18n";
import { capture } from "@/lib/analytics";

const ONBOARDING_KEY = "samvolt:onboarding";

// All page copy, keyed by locale. Headings use *asterisks* for the serif
// italic accent and \n for line breaks (rendered via renderAccented).
const COPY = {
  sv: {
    nav: { app: "App", compatibility: "Kompabilitet", features: "Funktioner", faq: "FAQ" },
    closeMenu: "Stäng meny",
    openMenu: "Öppna meny",
    help: "Hjälp",
    myAccount: "Mitt konto",
    bookDemo: "Boka demo",
    bookADemo: "Boka en demo",
    trialCta: "Testa utan kostnad",
    hero: {
      h1: "Optimera energin *automatiskt*\nför hela er *portfölj* av anläggningar.",
      sub: "AI-styrning för Solinteg & Sunwoda inklusive eftermarknad och säljverktyg för installatörer och säljföretag.",
      partners: "Finns hos bland annat:",
    },
    scrollReveal:
      "Vi tar över efter installationen. Samvolt Intelligence minskar supportärenden, ger full kontroll över kundportföljen och identifierar automatiskt när en anläggning behöver mer batterikapacitet – vilket skapar naturliga möjligheter till merförsäljning.",
 
    setup: {
      label: "Snabb installation",
      heading: "Igång på *minuter*",
      body: "Tre steg i appen – så är kundens anläggning uppkopplad.",
      steps: [
        {
          title: "Skanna elfakturan",
          body: "Skanna kundens elfaktura – AI:n fyller automatiskt i anläggningens uppgifter.",
        },
        {
          title: "Hämta adressen",
          body: "Hämta kundens adress och elområde automatiskt via positionen.",
        },
        {
          title: "Anslut & bjud in",
          body: "Lägg till enheter och skapa den första användaren – sedan är allt klart.",
        },
      ],
    },
    appViews: {
      heading: "Alla vyer i *en* app",
      body: "Dina kunder får en överskådlig app där produktion, batteri, förbrukning, effekt och besparing samlas på ett ställe – enkelt att förstå på några sekunder.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Byggd för installatörer",
        description:
          "En enkel och tydlig dashboard där du hanterar alla kunder och anläggningar från en och samma vy. Full överblick på sekunder – så att du snabbt kan agera och sköta eftermarknaden utan krångel.",
      },
      notifications: {
        title: "Automatiska aviseringar",
        subtitle: "Kunden uppdateras i realtid",
        description:
          "Automatiska notifieringar vid strömavbrott eller tappad kontakt håller kunden informerad. Resultatet är färre supportärenden och mindre tid på onödiga samtal.",
      },
      ai: {
        title: "AI för installatörer",
        subtitle: "Direkt i webbläsaren",
        description:
          "Felsök snabbare med AI. Beskriv problemet eller ange en felkod och få konkreta åtgärdsförslag direkt – utan att leta i manualer eller kontakta support.",
      },
      sweden: {
        badge: "Sverige",
        title: "Utvecklad i Sverige",
        body: "Svenskutvecklad med svensk serverdrift – byggd för hög säkerhet, dataskydd och driftsäkerhet.",
      },
    },
    inverter: {
      badge: "Tillgängligt nu",
      heading: "AI-styrning till kundernas *växelriktare* och *batteri*",
      body: "Erbjud Solinteg- och Sunwoda-kunder intelligent energistyrning som tillägg. Ni får portföljöversikt och eftermarknad – kunden får en anläggning som styrs som helhet, automatiskt.",
      note: "Tillgängligt för Solinteg och Sunwoda.",
    },
    pilotBadge: "Pilottestas · Kommer snart",
    car: {
      heading: "Ansluter *direkt* till dina kunders bilar",
      body: "Intelligence kopplas direkt till dina kunders bilar och läser av batterinivå och räckvidd i realtid – så att laddningen alltid planeras efter verklig körning, inte gissningar.",
      note: "Stöd för de flesta elbilsmärken.",
      imgAlt: "Elbil ansluten till Intelligence",
    },
    charging: {
      heading: "Laddar dina kunders bilar när det är *smartast*",
      body: "Intelligence planerar laddningen utifrån körning, solproduktion, batteri och elpris – så bilen är redo när kunden behöver den, utan manuell styrning.",
      note: "Fungerar med kundernas befintliga laddare.",
      imgAlt: "Närbild på elbil som laddar",
    },
    heating: {
      heading: "Värmer dina kunders *hem* när det är smartast",
      body: "Intelligence styr värmepumpen som del av hela anläggningen – väder, solproduktion, batteri och elpris – så komforten hålls utan onödig kostnad.",
      note: "Fungerar med kundernas befintliga värmepumpar.",
      imgAlt: "Värmepump styrd av Intelligence",
    },
    faq: { label: "Vanliga frågor", heading: "Allt du behöver *veta*" },
    faqItems: [
      {
        q: "Hur skapar Samvolt Intelligence affärsvärde?",
        a: "AI:n upptäcker anläggningar i behov av utbyggnad – mer sol, batteri eller laddning – och ger er naturliga merförsäljningar. Samtidigt minskar proaktiv eftermarknad både supportärenden och kundbortfall, så ni bygger återkommande relationer i stället för engångsjobb.",
      },
      {
        q: "Hur förenklar plattformen eftermarknaden?",
        a: "Ni ser status på alla anläggningar i realtid, och kunden får automatiskt besked när en enhet tappar kontakten eller vid strömavbrott. Färre oroliga samtal och mindre tid på att förklara läget – nöjdare kunder och en stabilare drift.",
      },
      {
        q: "Kan jag hantera flera anläggningar samtidigt?",
        a: "Ja. Plattformen är byggd för portföljer – styr, jämför och rapportera på flera anläggningar, regioner eller kunder från samma vy med roll- och behörighetsstyrning.",
      },
      {
        q: "Vilka växelriktare, batterier och system stöds?",
        a: "Intelligence integreras med växelriktare och batterier från Solinteg och Sunwoda, samt laddinfrastruktur. Ni kan också koppla mot befintliga energi- och affärssystem via API.",
      },
      {
        q: "Hur hanteras säkerhet och dataskydd?",
        a: "All data hanteras på svenska servrar enligt europeiska lagar och regler, inklusive GDPR. Inloggning sker med BankID, och plattformen är utvecklad i Sverige med dataskydd i fokus.",
      },
    ],
    cta: {
      heading: "Låt *portföljen* arbeta smartare",
      body: "Boka en demo och se hur Samvolt Intelligence optimerar varje anläggning och kilowattimme – automatiskt, dygnet runt.",
    },
    footer: {
      tagline: "Få ut mer av din energi\u00A0– *helt automatiskt*",
      developedInSweden: "Utvecklad i Sverige",
      product: "Produkt",
      company: "Företag",
      about: "Om oss",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alla rättigheter förbehållna.",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
    },
  },
  en: {
    nav: { app: "App", compatibility: "Compatibility", features: "Features", faq: "FAQ" },
    closeMenu: "Close menu",
    openMenu: "Open menu",
    help: "Help",
    myAccount: "My account",
    bookDemo: "Book a demo",
    bookADemo: "Book a demo",
    trialCta: "Try for free",
    hero: {
      h1: "Optimize energy *automatically*\nacross your entire *portfolio* of installations.",
      sub: "AI control and aftermarket for installers and sales teams – for Solinteg and Sunwoda.",
      partners: "Available through partners like:",
    },
    scrollReveal:
      "An installation doesn't end at commissioning. Samvolt Intelligence helps installers with natural upsells – the AI detects plants in need of expansion – while optimizing the entire customer portfolio around the clock. Fewer support tickets, happier customers and full control in real time.",
    setup: {
      label: "Quick setup",
      heading: "Up and running in *minutes*",
      body: "Three steps in the app – and the customer's plant is connected.",
      steps: [
        {
          title: "Scan the electricity bill",
          body: "Scan the customer's electricity bill – the AI fills in the plant's details automatically.",
        },
        {
          title: "Get the address",
          body: "Get the customer's address and electricity price area automatically via your location.",
        },
        {
          title: "Connect & invite",
          body: "Add devices and create the first user – then everything is ready.",
        },
      ],
    },
    appViews: {
      heading: "Every view in *one* app",
      body: "Your customers get a clear, easy-to-grasp app where production, battery, consumption, power and savings come together in one place.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Built for installers",
        description:
          "A simple, clear dashboard where you manage all customers and plants from a single view. Full overview in seconds – so you can act quickly and handle the aftermarket without hassle.",
      },
      notifications: {
        title: "Automatic notifications",
        subtitle: "Customers updated in real time",
        description:
          "Automatic notifications on power outages or lost connection keep the customer informed. The result is fewer support tickets and less time on unnecessary calls.",
      },
      ai: {
        title: "AI for installers",
        subtitle: "Right in the browser",
        description:
          "Troubleshoot faster with AI. Describe the problem or enter an error code and get concrete fixes instantly – without digging through manuals or contacting support.",
      },
      sweden: {
        badge: "Sweden",
        title: "Developed in Sweden",
        body: "Swedish-built with Swedish server operations – engineered for high security, data protection and reliability.",
      },
    },
    inverter: {
      badge: "Available now",
      heading: "AI control for your customers' *inverters* and *batteries*",
      body: "Offer Solinteg and Sunwoda customers intelligent energy management as an add-on. You get portfolio overview and aftermarket – the customer gets an installation that is controlled as a whole, automatically.",
      note: "Available for Solinteg and Sunwoda.",
    },
    pilotBadge: "In pilot testing · Coming soon",
    car: {
      heading: "Connects *directly* to your customers' cars",
      body: "Intelligence connects directly to your customers' cars and reads battery level and range in real time – so charging is always planned around actual driving, not guesswork.",
      note: "Supports most EV brands.",
      imgAlt: "Electric car connected to Intelligence",
    },
    charging: {
      heading: "Charges your customers' cars when it's *smartest*",
      body: "Intelligence plans charging around driving, solar production, battery and electricity prices – so the car is ready when the customer needs it, without any manual control.",
      note: "Works with customers' existing chargers.",
      imgAlt: "Close-up of an electric car charging",
    },
    heating: {
      heading: "Heats your customers' *homes* when it's smartest",
      body: "Intelligence controls the heat pump as part of the whole installation – weather, solar production, battery and electricity prices – so comfort is maintained without unnecessary cost.",
      note: "Works with customers' existing heat pumps.",
      imgAlt: "Heat pump controlled by Intelligence",
    },
    faq: { label: "Frequently asked questions", heading: "Everything you need to *know*" },
    faqItems: [
      {
        q: "What is Samvolt Intelligence for installers?",
        a: "A platform that helps you manage your entire customer portfolio after installation – monitor, optimize and act before the customer even notices something is wrong. More time for new business, less time on support.",
      },
      {
        q: "How does it create business value for us?",
        a: "The AI detects plants in need of expansion – more solar, battery or charging – giving you natural upsells. At the same time, proactive aftermarket care reduces both support tickets and customer churn, so you build recurring relationships instead of one-off jobs.",
      },
      {
        q: "How does it help us in the aftermarket?",
        a: "You see the status of all plants in real time, and the customer is automatically notified when a device loses connection or during power outages. Fewer worried calls and less time explaining the situation – happier customers and more stable operations.",
      },
      {
        q: "Can you handle multiple plants?",
        a: "Yes. The platform is built for portfolios – control, compare and report on multiple plants, regions or customers from the same view, with roles and permissions.",
      },
      {
        q: "What equipment and systems does it work with?",
        a: "Intelligence integrates with Solinteg and Sunwoda inverters and batteries, as well as charging infrastructure. You can also connect to existing energy and business systems via API.",
      },
      {
        q: "How are security and data protection handled?",
        a: "All data is handled on Swedish servers in accordance with European laws and regulations, including GDPR. Sign-in is through BankID, and the platform is developed in Sweden with data protection in focus.",
      },
    ],
    cta: {
      heading: "Let your *portfolio* work smarter",
      body: "Book a demo and see how Samvolt Intelligence optimizes every plant and kilowatt-hour – automatically, around the clock.",
    },
    footer: {
      tagline: "Get more out of your energy\u00A0– *fully automatic*",
      developedInSweden: "Developed in Sweden",
      product: "Product",
      company: "Company",
      about: "About us",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. All rights reserved.",
      privacy: "Privacy policy",
      terms: "Terms of use",
    },
  },
  no: {
    nav: { app: "App", compatibility: "Kompatibilitet", features: "Funksjoner", faq: "FAQ" },
    closeMenu: "Lukk meny",
    openMenu: "Åpne meny",
    help: "Hjelp",
    myAccount: "Min konto",
    bookDemo: "Bestill demo",
    bookADemo: "Bestill en demo",
    trialCta: "Prøv gratis",
    hero: {
      h1: "Optimaliser energien *automatisk*\nover hele *porteføljen* av anlegg.",
      sub: "AI-styring og ettermarked for installatører og salgsteam – for Solinteg og Sunwoda.",
      partners: "Tilgjengelig via partnere som:",
    },
    scrollReveal:
      "Et anlegg slutter ikke ved idriftsettelse. Samvolt Intelligence hjelper installatører med naturlige mersalg – AI-en oppdager anlegg som trenger utvidelse – samtidig som hele kundeporteføljen optimaliseres døgnet rundt. Færre supportsaker, fornøyde kunder og full kontroll i sanntid.",
    setup: {
      label: "Rask oppsett",
      heading: "I gang på *minutter*",
      body: "Tre steg i appen – så er kundens anlegg tilkoblet.",
      steps: [
        {
          title: "Skann strømregningen",
          body: "Skann kundens strømregning – AI-en fyller inn anleggets detaljer automatisk.",
        },
        {
          title: "Hent adressen",
          body: "Hent kundens adresse og prisområde automatisk via posisjonen din.",
        },
        {
          title: "Koble til og inviter",
          body: "Legg til enheter og opprett den første brukeren – deretter er alt klart.",
        },
      ],
    },
    appViews: {
      heading: "Alle visninger i *én* app",
      body: "Kundene dine får en oversiktlig app der produksjon, batteri, forbruk, effekt og besparelse samles på ett sted.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Bygget for installatører",
        description:
          "Et enkelt og tydelig dashboard der du administrerer alle kunder og anlegg fra én visning. Full oversikt på sekunder – så du raskt kan handle og håndtere ettermarkedet uten krøll.",
      },
      notifications: {
        title: "Automatiske varsler",
        subtitle: "Kundene oppdateres i sanntid",
        description:
          "Automatiske varsler ved strømbrudd eller tapt kontakt holder kunden informert. Resultatet er færre supportsaker og mindre tid på unødvendige samtaler.",
      },
      ai: {
        title: "AI for installatører",
        subtitle: "Rett i nettleseren",
        description:
          "Feilsøk raskere med AI. Beskriv problemet eller oppgi en feilkode og få konkrete tiltak umiddelbart – uten å lete i manualer eller kontakte support.",
      },
      sweden: {
        badge: "Sverige",
        title: "Utviklet i Sverige",
        body: "Svenskutviklet med svensk serverdrift – bygget for høy sikkerhet, personvern og driftsstabilitet.",
      },
    },
    inverter: {
      badge: "Tilgjengelig nå",
      heading: "AI-styring for kundenes *vekselrettere* og *batterier*",
      body: "Tilby Solinteg- og Sunwoda-kunder intelligent energistyring som tillegg. Dere får porteføljeoversikt og ettermarked – kunden får et anlegg som styres som en helhet, automatisk.",
      note: "Tilgjengelig for Solinteg og Sunwoda.",
    },
    pilotBadge: "I pilottesting · Kommer snart",
    car: {
      heading: "Kobler *direkte* til kundenes biler",
      body: "Intelligence kobles direkte til kundenes biler og leser batterinivå og rekkevidde i sanntid – så ladingen alltid planlegges etter faktisk kjøring, ikke gjetting.",
      note: "Støtter de fleste elbilmerker.",
      imgAlt: "Elbil koblet til Intelligence",
    },
    charging: {
      heading: "Lader kundenes biler når det er *smartest*",
      body: "Intelligence planlegger ladingen ut fra kjøring, solproduksjon, batteri og strømpris – så bilen er klar når kunden trenger den, uten manuell styring.",
      note: "Fungerer med kundenes eksisterende ladere.",
      imgAlt: "Nærbilde av elbil som lader",
    },
    heating: {
      heading: "Varmer kundenes *hjem* når det er smartest",
      body: "Intelligence styrer varmepumpen som del av hele anlegget – vær, solproduksjon, batteri og strømpris – så komforten holdes uten unødvendig kostnad.",
      note: "Fungerer med kundenes eksisterende varmepumper.",
      imgAlt: "Varmepumpe styrt av Intelligence",
    },
    faq: { label: "Ofte stilte spørsmål", heading: "Alt du trenger å *vite*" },
    faqItems: [
      {
        q: "Hva er Samvolt Intelligence for installatører?",
        a: "En plattform som hjelper deg å forvalte hele kundeporteføljen etter installasjonen – overvåke, optimalisere og handle før kunden merker at noe er galt. Mer tid til nye forretninger, mindre tid på support.",
      },
      {
        q: "Hvordan skaper det forretningsverdi for oss?",
        a: "AI-en oppdager anlegg som trenger utvidelse – mer sol, batteri eller lading – og gir naturlige mersalg. Samtidig reduserer proaktiv ettermarked både supportsaker og kundetap, så dere bygger gjentakende relasjoner i stedet for engangsjobber.",
      },
      {
        q: "Hvordan hjelper det oss i ettermarkedet?",
        a: "Dere ser status på alle anlegg i sanntid, og kunden varsles automatisk når en enhet mister kontakt eller ved strømbrudd. Færre bekymrede samtaler og mindre tid på å forklare situasjonen – fornøyde kunder og mer stabil drift.",
      },
      {
        q: "Kan dere håndtere flere anlegg?",
        a: "Ja. Plattformen er bygget for porteføljer – styr, sammenlign og rapporter på flere anlegg, regioner eller kunder fra samme visning, med roller og tilganger.",
      },
      {
        q: "Hvilket utstyr og hvilke systemer fungerer det med?",
        a: "Intelligence integreres med Solinteg- og Sunwoda-vekselrettere og -batterier, samt ladeinfrastruktur. Dere kan også koble til eksisterende energi- og forretningssystemer via API.",
      },
      {
        q: "Hvordan håndteres sikkerhet og personvern?",
        a: "All data håndteres på svenske servere i henhold til europeiske lover og regler, inkludert GDPR. Innlogging skjer med BankID, og plattformen er utviklet i Sverige med personvern i fokus.",
      },
    ],
    cta: {
      heading: "La *porteføljen* jobbe smartere",
      body: "Bestill en demo og se hvordan Samvolt Intelligence optimaliserer hvert anlegg og kilowattime – automatisk, døgnet rundt.",
    },
    footer: {
      tagline: "Få mer ut av energien din\u00A0– *helt automatisk*",
      developedInSweden: "Utviklet i Sverige",
      product: "Produkt",
      company: "Selskap",
      about: "Om oss",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle rettigheter forbeholdt.",
      privacy: "Personvernerklæring",
      terms: "Brukervilkår",
    },
  },
  da: {
    nav: { app: "App", compatibility: "Kompatibilitet", features: "Funktioner", faq: "FAQ" },
    closeMenu: "Luk menu",
    openMenu: "Åbn menu",
    help: "Hjælp",
    myAccount: "Min konto",
    bookDemo: "Book demo",
    bookADemo: "Book en demo",
    trialCta: "Prøv gratis",
    hero: {
      h1: "Optimer energien *automatisk*\npå hele *porteføljen* af anlæg.",
      sub: "AI-styring og eftermarked for installatører og salgsteams – til Solinteg og Sunwoda.",
      partners: "Tilgængelig via partnere som:",
    },
    scrollReveal:
      "Et anlæg slutter ikke ved idriftsættelse. Samvolt Intelligence hjælper installatører med naturlige mersalg – AI’en finder anlæg, der har brug for udvidelse – samtidig med at hele kundeporteføljen optimeres døgnet rundt. Færre supportsager, gladere kunder og fuld kontrol i realtid.",
    setup: {
      label: "Hurtig opsætning",
      heading: "I gang på *minutter*",
      body: "Tre trin i appen – så er kundens anlæg tilsluttet.",
      steps: [
        {
          title: "Scan elregningen",
          body: "Scan kundens elregning – AI’en udfylder anlæggets oplysninger automatisk.",
        },
        {
          title: "Hent adressen",
          body: "Hent kundens adresse og prisområde automatisk via din position.",
        },
        {
          title: "Tilslut og inviter",
          body: "Tilføj enheder og opret den første bruger – derefter er alt klar.",
        },
      ],
    },
    appViews: {
      heading: "Alle visninger i *én* app",
      body: "Dine kunder får en overskuelig app, hvor produktion, batteri, forbrug, effekt og besparelse samles ét sted.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Bygget til installatører",
        description:
          "Et enkelt og klart dashboard, hvor du administrerer alle kunder og anlæg fra én visning. Fuldt overblik på sekunder – så du hurtigt kan handle og håndtere eftermarkedet uden besvær.",
      },
      notifications: {
        title: "Automatiske notifikationer",
        subtitle: "Kunderne opdateres i realtid",
        description:
          "Automatiske notifikationer ved strømafbrydelse eller mistet forbindelse holder kunden informeret. Resultatet er færre supportsager og mindre tid på unødvendige opkald.",
      },
      ai: {
        title: "AI til installatører",
        subtitle: "Direkte i browseren",
        description:
          "Fejlfind hurtigere med AI. Beskriv problemet eller angiv en fejlkode og få konkrete løsninger med det samme – uden at lede i manualer eller kontakte support.",
      },
      sweden: {
        badge: "Sverige",
        title: "Udviklet i Sverige",
        body: "Svenskudviklet med svensk serverdrift – bygget til høj sikkerhed, databeskyttelse og driftsstabilitet.",
      },
    },
    inverter: {
      badge: "Tilgængelig nu",
      heading: "AI-styring til kundernes *vekselrettere* og *batterier*",
      body: "Tilbyd Solinteg- og Sunwoda-kunder intelligent energistyring som tillæg. I får porteføljeoverblik og eftermarked – kunden får et anlæg, der styres som en helhed, automatisk.",
      note: "Tilgængelig til Solinteg og Sunwoda.",
    },
    pilotBadge: "I pilottest · Kommer snart",
    car: {
      heading: "Forbinder *direkte* til kundernes biler",
      body: "Intelligence forbinder direkte til kundernes biler og aflæser batteriniveau og rækkevidde i realtid – så opladningen altid planlægges efter faktisk kørsel, ikke gætterier.",
      note: "Understøtter de fleste elbilmærker.",
      imgAlt: "Elbil forbundet til Intelligence",
    },
    charging: {
      heading: "Oplader kundernes biler, når det er *smartest*",
      body: "Intelligence planlægger opladningen ud fra kørsel, solproduktion, batteri og elpris – så bilen er klar, når kunden har brug for den, uden manuel styring.",
      note: "Fungerer med kundernes eksisterende ladere.",
      imgAlt: "Nærbillede af elbil, der oplader",
    },
    heating: {
      heading: "Opvarmer kundernes *hjem*, når det er smartest",
      body: "Intelligence styrer varmepumpen som del af hele anlægget – vejr, solproduktion, batteri og elpris – så komforten bevares uden unødvendig omkostning.",
      note: "Fungerer med kundernes eksisterende varmepumper.",
      imgAlt: "Varmepumpe styret af Intelligence",
    },
    faq: { label: "Ofte stillede spørgsmål", heading: "Alt du behøver at *vide*" },
    faqItems: [
      {
        q: "Hvad er Samvolt Intelligence for installatører?",
        a: "En platform, der hjælper dig med at administrere hele kundeporteføljen efter installationen – overvåge, optimere og handle, før kunden overhovedet bemærker, at noget er galt. Mere tid til nye forretninger, mindre tid på support.",
      },
      {
        q: "Hvordan skaber det forretningsværdi for os?",
        a: "AI’en finder anlæg, der har brug for udvidelse – mere sol, batteri eller opladning – og giver naturlige mersalg. Samtidig reducerer proaktivt eftermarked både supportsager og kundeafgang, så I bygger tilbagevendende relationer i stedet for engangsopgaver.",
      },
      {
        q: "Hvordan hjælper det os i eftermarkedet?",
        a: "I ser status på alle anlæg i realtid, og kunden får automatisk besked, når en enhed mister forbindelsen eller ved strømafbrydelse. Færre bekymrede opkald og mindre tid på at forklare situationen – gladere kunder og mere stabil drift.",
      },
      {
        q: "Kan I håndtere flere anlæg?",
        a: "Ja. Platformen er bygget til porteføljer – styr, sammenlign og rapporter på flere anlæg, regioner eller kunder fra samme visning, med roller og rettigheder.",
      },
      {
        q: "Hvilket udstyr og hvilke systemer fungerer det med?",
        a: "Intelligence integreres med Solinteg- og Sunwoda-vekselrettere og -batterier samt ladeinfrastruktur. I kan også koble til eksisterende energi- og forretningssystemer via API.",
      },
      {
        q: "Hvordan håndteres sikkerhed og databeskyttelse?",
        a: "Alle data håndteres på svenske servere i overensstemmelse med europæiske love og regler, herunder GDPR. Login sker med BankID, og platformen er udviklet i Sverige med databeskyttelse i fokus.",
      },
    ],
    cta: {
      heading: "Lad *porteføljen* arbejde smartere",
      body: "Book en demo og se, hvordan Samvolt Intelligence optimerer hvert anlæg og kilowatttime – automatisk, døgnet rundt.",
    },
    footer: {
      tagline: "Få mere ud af din energi\u00A0– *fuldt automatisk*",
      developedInSweden: "Udviklet i Sverige",
      product: "Produkt",
      company: "Virksomhed",
      about: "Om os",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle rettigheder forbeholdes.",
      privacy: "Privatlivspolitik",
      terms: "Brugervilkår",
    },
  },
  fi: {
    nav: { app: "Sovellus", compatibility: "Yhteensopivuus", features: "Ominaisuudet", faq: "UKK" },
    closeMenu: "Sulje valikko",
    openMenu: "Avaa valikko",
    help: "Ohje",
    myAccount: "Oma tili",
    bookDemo: "Varaa demo",
    bookADemo: "Varaa demo",
    trialCta: "Kokeile ilmaiseksi",
    hero: {
      h1: "Optimoi energia *automaattisesti*\nkoko *portfolion* laitoksille.",
      sub: "Tekoälyohjaus ja jälkimarkkina asentajille sekä myyntitiimeille – Solintegille ja Sunwodalle.",
      partners: "Saatavilla muun muassa näiden kautta:",
    },
    scrollReveal:
      "Laitos ei lopu käyttöönottoon. Samvolt Intelligence auttaa asentajia luonnollisessa lisämyynnissä – tekoäly tunnistaa laajennusta tarvitsevat laitokset – samalla kun koko asiakasportfolio optimoidaan ympäri vuorokauden. Vähemmän tukipyyntöjä, tyytyväisemmät asiakkaat ja täysi hallinta reaaliajassa.",
    setup: {
      label: "Nopea käyttöönotto",
      heading: "Käynnissä *minuuteissa*",
      body: "Kolme vaihetta sovelluksessa – ja asiakkaan laitos on yhdistetty.",
      steps: [
        {
          title: "Skannaa sähkölasku",
          body: "Skannaa asiakkaan sähkölasku – tekoäly täyttää laitoksen tiedot automaattisesti.",
        },
        {
          title: "Hae osoite",
          body: "Hae asiakkaan osoite ja hinta-alue automaattisesti sijaintisi avulla.",
        },
        {
          title: "Yhdistä ja kutsu",
          body: "Lisää laitteet ja luo ensimmäinen käyttäjä – sitten kaikki on valmista.",
        },
      ],
    },
    appViews: {
      heading: "Kaikki näkymät *yhdessä* sovelluksessa",
      body: "Asiakkaasi saavat selkeän sovelluksen, jossa tuotanto, akku, kulutus, teho ja säästöt ovat yhdessä paikassa.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Rakennettu asentajille",
        description:
          "Yksinkertainen ja selkeä dashboard, jossa hallitset kaikki asiakkaat ja laitokset yhdestä näkymästä. Täysi yleiskuva sekunneissa – jotta voit toimia nopeasti ja hoitaa jälkimarkkinan vaivattomasti.",
      },
      notifications: {
        title: "Automaattiset ilmoitukset",
        subtitle: "Asiakkaat ajan tasalla reaaliajassa",
        description:
          "Automaattiset ilmoitukset sähkökatkosta tai menetystä yhteydestä pitävät asiakkaan ajan tasalla. Tuloksena on vähemmän tukipyyntöjä ja vähemmän aikaa turhiin puheluihin.",
      },
      ai: {
        title: "Tekoäly asentajille",
        subtitle: "Suoraan selaimessa",
        description:
          "Vianmääritys nopeammin tekoälyllä. Kuvaa ongelma tai anna virhekoodi ja saat konkreettiset toimenpiteet heti – ilman manuaaleja tai tukipyyntöjä.",
      },
      sweden: {
        badge: "Ruotsi",
        title: "Kehitetty Ruotsissa",
        body: "Ruotsissa kehitetty ruotsalaisella palvelinajolla – rakennettu korkeaan turvallisuuteen, tietosuojaan ja toimintavarmuuteen.",
      },
    },
    inverter: {
      badge: "Saatavilla nyt",
      heading: "Tekoälyohjaus asiakkaiden *inverttereille* ja *akuille*",
      body: "Tarjoa Solinteg- ja Sunwoda-asiakkaille älykästä energianhallintaa lisänä. Saat portfolion yleiskuvan ja jälkimarkkinan – asiakas saa laitoksen, jota ohjataan kokonaisuutena, automaattisesti.",
      note: "Saatavilla Solintegille ja Sunwodalle.",
    },
    pilotBadge: "Pilotissa · Tulossa pian",
    car: {
      heading: "Yhdistyy *suoraan* asiakkaiden autoihin",
      body: "Intelligence yhdistyy suoraan asiakkaiden autoihin ja lukee akun varauksen sekä toimintamatkan reaaliajassa – joten lataus suunnitellaan aina todellisen ajon mukaan, ei arvailujen.",
      note: "Tukee useimpia sähköautomerkkejä.",
      imgAlt: "Sähköauto yhdistettynä Intelligenceen",
    },
    charging: {
      heading: "Lataa asiakkaiden autot, kun se on *älykkäintä*",
      body: "Intelligence suunnittelee latauksen ajon, aurinkotuotannon, akun ja sähkön hinnan perusteella – jotta auto on valmis, kun asiakas sitä tarvitsee, ilman manuaalista ohjausta.",
      note: "Toimii asiakkaiden olemassa olevien lataajien kanssa.",
      imgAlt: "Lähikuva lataavasta sähköautosta",
    },
    heating: {
      heading: "Lämmittää asiakkaiden *kodit*, kun se on älykkäintä",
      body: "Intelligence ohjaa lämpöpumppua osana koko laitosta – sää, aurinkotuotanto, akku ja sähkön hinta – jotta mukavuus säilyy ilman turhia kustannuksia.",
      note: "Toimii asiakkaiden olemassa olevien lämpöpumppujen kanssa.",
      imgAlt: "Intelligence-ohjattu lämpöpumppu",
    },
    faq: { label: "Usein kysytyt kysymykset", heading: "Kaikki mitä tarvitset *tietää*" },
    faqItems: [
      {
        q: "Mikä Samvolt Intelligence on asentajille?",
        a: "Alusta, joka auttaa hallitsemaan koko asiakasportfoliota asennuksen jälkeen – valvomaan, optimoimaan ja toimimaan ennen kuin asiakas edes huomaa ongelmaa. Enemmän aikaa uusille kaupoille, vähemmän aikaa tukeen.",
      },
      {
        q: "Miten se luo liiketoiminta-arvoa meille?",
        a: "Tekoäly tunnistaa laajennusta tarvitsevat laitokset – lisää aurinkoa, akkua tai latausta – ja tarjoaa luonnollista lisämyyntiä. Samalla ennakoiva jälkimarkkina vähentää sekä tukipyyntöjä että asiakaspoistumaa, joten rakennatte toistuvia suhteita kertaluonteisten töiden sijaan.",
      },
      {
        q: "Miten se auttaa meitä jälkimarkkinassa?",
        a: "Näette kaikkien laitosten tilan reaaliajassa, ja asiakas saa automaattisesti ilmoituksen, kun laite menettää yhteyden tai sähköt katkeavat. Vähemmän huolestuneita puheluita ja vähemmän aikaa tilanteen selittämiseen – tyytyväisemmät asiakkaat ja vakaampi toiminta.",
      },
      {
        q: "Voitteko hallita useita laitoksia?",
        a: "Kyllä. Alusta on rakennettu portfolioille – ohjaa, vertaa ja raportoi useita laitoksia, alueita tai asiakkaita samasta näkymästä rooleilla ja käyttöoikeuksilla.",
      },
      {
        q: "Minkä laitteiden ja järjestelmien kanssa se toimii?",
        a: "Intelligence integroituu Solintegin ja Sunwodan inverttereihin ja akkuihin sekä latausinfrastruktuuriin. Voitte myös yhdistää olemassa oleviin energia- ja liiketoimintajärjestelmiin API:n kautta.",
      },
      {
        q: "Miten turvallisuus ja tietosuoja hoidetaan?",
        a: "Kaikki data käsitellään ruotsalaisilla palvelimilla Euroopan lakien ja sääntöjen, mukaan lukien GDPR:n, mukaisesti. Kirjautuminen tapahtuu BankID:llä, ja alusta on kehitetty Ruotsissa tietosuoja edellä.",
      },
    ],
    cta: {
      heading: "Anna *portfolion* työskennellä älykkäämmin",
      body: "Varaa demo ja katso, miten Samvolt Intelligence optimoi jokaisen laitoksen ja kilowattitunnin – automaattisesti, ympäri vuorokauden.",
    },
    footer: {
      tagline: "Saa enemmän irti energiastasi\u00A0– *täysin automaattisesti*",
      developedInSweden: "Kehitetty Ruotsissa",
      product: "Tuote",
      company: "Yritys",
      about: "Tietoa meistä",
      support: "Tuki",
      rights: "© 2026 Samvolt Intelligence. Kaikki oikeudet pidätetään.",
      privacy: "Tietosuojaseloste",
      terms: "Käyttöehdot",
    },
  },
  de: {
    nav: { app: "App", compatibility: "Kompatibilität", features: "Funktionen", faq: "FAQ" },
    closeMenu: "Menü schließen",
    openMenu: "Menü öffnen",
    help: "Hilfe",
    myAccount: "Mein Konto",
    bookDemo: "Demo buchen",
    bookADemo: "Eine Demo buchen",
    trialCta: "Kostenlos testen",
    hero: {
      h1: "Energie *automatisch* optimieren\nüber Ihr gesamtes *Portfolio* an Anlagen.",
      sub: "KI-Steuerung und Aftermarket für Installateure und Vertriebsteams – für Solinteg und Sunwoda.",
      partners: "Verfügbar über Partner wie:",
    },
    scrollReveal:
      "Eine Anlage endet nicht mit der Inbetriebnahme. Samvolt Intelligence hilft Installateuren mit natürlichen Zusatzverkäufen – die KI erkennt Anlagen mit Erweiterungsbedarf – und optimiert gleichzeitig das gesamte Kundenportfolio rund um die Uhr. Weniger Supportfälle, zufriedenere Kunden und volle Kontrolle in Echtzeit.",
    setup: {
      label: "Schnelle Einrichtung",
      heading: "In *Minuten* startklar",
      body: "Drei Schritte in der App – und die Anlage des Kunden ist verbunden.",
      steps: [
        {
          title: "Stromrechnung scannen",
          body: "Scannen Sie die Stromrechnung des Kunden – die KI füllt die Anlagendetails automatisch aus.",
        },
        {
          title: "Adresse abrufen",
          body: "Abrufen von Adresse und Preisgebiet des Kunden automatisch über Ihren Standort.",
        },
        {
          title: "Verbinden & einladen",
          body: "Geräte hinzufügen und den ersten Benutzer anlegen – dann ist alles bereit.",
        },
      ],
    },
    appViews: {
      heading: "Jede Ansicht in *einer* App",
      body: "Ihre Kunden erhalten eine übersichtliche App, in der Produktion, Batterie, Verbrauch, Leistung und Einsparungen an einem Ort zusammenkommen.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Für Installateure gebaut",
        description:
          "Ein einfaches, klares Dashboard, in dem Sie alle Kunden und Anlagen aus einer Ansicht verwalten. Voller Überblick in Sekunden – damit Sie schnell handeln und den Aftermarket ohne Aufwand steuern können.",
      },
      notifications: {
        title: "Automatische Benachrichtigungen",
        subtitle: "Kunden in Echtzeit informiert",
        description:
          "Automatische Benachrichtigungen bei Stromausfall oder Verbindungsverlust halten den Kunden informiert. Das Ergebnis: weniger Supportfälle und weniger Zeit für unnötige Anrufe.",
      },
      ai: {
        title: "KI für Installateure",
        subtitle: "Direkt im Browser",
        description:
          "Schneller Fehlerdiagnose mit KI. Beschreiben Sie das Problem oder geben Sie einen Fehlercode ein und erhalten Sie sofort konkrete Maßnahmen – ohne Manuale oder Supportkontakt.",
      },
      sweden: {
        badge: "Schweden",
        title: "Entwickelt in Schweden",
        body: "In Schweden entwickelt mit schwedischem Serverbetrieb – ausgelegt auf hohe Sicherheit, Datenschutz und Betriebssicherheit.",
      },
    },
    inverter: {
      badge: "Jetzt verfügbar",
      heading: "KI-Steuerung für die *Wechselrichter* und *Batterien* Ihrer Kunden",
      body: "Bieten Sie Solinteg- und Sunwoda-Kunden intelligentes Energiemanagement als Zusatz. Sie erhalten Portfolioübersicht und Aftermarket – der Kunde erhält eine Anlage, die als Ganzes automatisch gesteuert wird.",
      note: "Verfügbar für Solinteg und Sunwoda.",
    },
    pilotBadge: "Im Pilotbetrieb · Demnächst verfügbar",
    car: {
      heading: "Verbindet sich *direkt* mit den Autos Ihrer Kunden",
      body: "Intelligence verbindet sich direkt mit den Autos Ihrer Kunden und liest Batteriestand und Reichweite in Echtzeit – damit das Laden immer nach dem tatsächlichen Fahrverhalten geplant wird, nicht nach Schätzungen.",
      note: "Unterstützt die meisten EV-Marken.",
      imgAlt: "Elektroauto mit Intelligence verbunden",
    },
    charging: {
      heading: "Lädt die Autos Ihrer Kunden, wenn es *am sinnvollsten* ist",
      body: "Intelligence plant das Laden anhand von Fahrten, Solarproduktion, Batterie und Strompreis – damit das Auto bereit ist, wenn der Kunde es braucht, ohne manuelle Steuerung.",
      note: "Funktioniert mit den vorhandenen Ladegeräten der Kunden.",
      imgAlt: "Nahaufnahme eines ladenden Elektroautos",
    },
    heating: {
      heading: "Heizt die *Häuser* Ihrer Kunden, wenn es am sinnvollsten ist",
      body: "Intelligence steuert die Wärmepumpe als Teil der gesamten Anlage – Wetter, Solarproduktion, Batterie und Strompreis – damit Komfort ohne unnötige Kosten erhalten bleibt.",
      note: "Funktioniert mit den vorhandenen Wärmepumpen der Kunden.",
      imgAlt: "Von Intelligence gesteuerte Wärmepumpe",
    },
    faq: { label: "Häufig gestellte Fragen", heading: "Alles, was Sie *wissen* müssen" },
    faqItems: [
      {
        q: "Was ist Samvolt Intelligence für Installateure?",
        a: "Eine Plattform, mit der Sie nach der Installation Ihr gesamtes Kundenportfolio verwalten – überwachen, optimieren und handeln, bevor der Kunde überhaupt merkt, dass etwas nicht stimmt. Mehr Zeit für neues Geschäft, weniger Zeit für Support.",
      },
      {
        q: "Wie schafft es Geschäftswert für uns?",
        a: "Die KI erkennt Anlagen mit Erweiterungsbedarf – mehr Solar, Batterie oder Laden – und liefert natürliche Zusatzverkäufe. Gleichzeitig senkt proaktiver Aftermarket sowohl Supportfälle als auch Kundenabwanderung, sodass Sie wiederkehrende Beziehungen statt Einmaljobs aufbauen.",
      },
      {
        q: "Wie hilft es uns im Aftermarket?",
        a: "Sie sehen den Status aller Anlagen in Echtzeit, und der Kunde wird automatisch benachrichtigt, wenn ein Gerät die Verbindung verliert oder bei Stromausfall. Weniger besorgte Anrufe und weniger Zeit für Erklärungen – zufriedenere Kunden und stabilerer Betrieb.",
      },
      {
        q: "Können Sie mehrere Anlagen verwalten?",
        a: "Ja. Die Plattform ist für Portfolios gebaut – steuern, vergleichen und berichten Sie über mehrere Anlagen, Regionen oder Kunden aus derselben Ansicht, mit Rollen und Berechtigungen.",
      },
      {
        q: "Mit welchen Geräten und Systemen funktioniert es?",
        a: "Intelligence integriert sich mit Solinteg- und Sunwoda-Wechselrichtern und -Batterien sowie Ladeinfrastruktur. Sie können auch bestehende Energie- und Geschäftssysteme über API anbinden.",
      },
      {
        q: "Wie werden Sicherheit und Datenschutz gehandhabt?",
        a: "Alle Daten werden auf schwedischen Servern gemäß europäischen Gesetzen und Vorschriften einschließlich DSGVO verarbeitet. Die Anmeldung erfolgt über BankID, und die Plattform wird in Schweden mit Fokus auf Datenschutz entwickelt.",
      },
    ],
    cta: {
      heading: "Lassen Sie Ihr *Portfolio* smarter arbeiten",
      body: "Buchen Sie eine Demo und sehen Sie, wie Samvolt Intelligence jede Anlage und Kilowattstunde optimiert – automatisch, rund um die Uhr.",
    },
    footer: {
      tagline: "Holen Sie mehr aus Ihrer Energie\u00A0– *vollautomatisch*",
      developedInSweden: "Entwickelt in Schweden",
      product: "Produkt",
      company: "Unternehmen",
      about: "Über uns",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle Rechte vorbehalten.",
      privacy: "Datenschutzerklärung",
      terms: "Nutzungsbedingungen",
    },
  },
  cs: {
    nav: { app: "Aplikace", compatibility: "Kompatibilita", features: "Funkce", faq: "FAQ" },
    closeMenu: "Zavřít menu",
    openMenu: "Otevřít menu",
    help: "Nápověda",
    myAccount: "Můj účet",
    bookDemo: "Objednat demo",
    bookADemo: "Objednat demo",
    trialCta: "Vyzkoušet zdarma",
    hero: {
      h1: "Optimalizujte energii *automaticky*\npřes celé *portfolio* instalací.",
      sub: "AI řízení a aftermarket pro instalační firmy a prodejní týmy – pro Solinteg a Sunwoda.",
      partners: "Dostupné u partnerů jako:",
    },
    scrollReveal:
      "Instalace nekončí uvedením do provozu. Samvolt Intelligence pomáhá instalatérům s přirozeným doprodejem – AI rozpozná instalace potřebující rozšíření – a zároveň optimalizuje celé zákaznické portfolio nonstop. Méně servisních požadavků, spokojenější zákazníci a plná kontrola v reálném čase.",
    setup: {
      label: "Rychlé nastavení",
      heading: "V provozu během *minut*",
      body: "Tři kroky v aplikaci – a zákazníkova instalace je připojená.",
      steps: [
        {
          title: "Naskenujte fakturu za elektřinu",
          body: "Naskenujte zákazníkovu fakturu za elektřinu – AI automaticky vyplní údaje o instalaci.",
        },
        {
          title: "Získat adresu",
          body: "Získejte adresu zákazníka a cenovou oblast automaticky přes vaši polohu.",
        },
        {
          title: "Připojit a pozvat",
          body: "Přidejte zařízení a vytvořte prvního uživatele – pak je vše připraveno.",
        },
      ],
    },
    appViews: {
      heading: "Všechny pohledy v *jedné* aplikaci",
      body: "Vaši zákazníci získají přehlednou aplikaci, kde jsou výroba, baterie, spotřeba, výkon a úspory na jednom místě.",
    },
    bento: {
      dashboard: {
        title: "Dashboard",
        subtitle: "Postaveno pro instalatéry",
        description:
          "Jednoduchý a přehledný dashboard, kde spravujete všechny zákazníky a instalace z jednoho pohledu. Úplný přehled během sekund – abyste mohli rychle jednat a zvládat aftermarket bez komplikací.",
      },
      notifications: {
        title: "Automatická oznámení",
        subtitle: "Zákazníci aktualizováni v reálném čase",
        description:
          "Automatická oznámení při výpadku proudu nebo ztrátě spojení udržují zákazníka informovaného. Výsledkem je méně servisních požadavků a méně času na zbytečné hovory.",
      },
      ai: {
        title: "AI pro instalatéry",
        subtitle: "Přímo v prohlížeči",
        description:
          "Odstraňujte závady rychleji s AI. Popište problém nebo zadejte chybový kód a získejte konkrétní řešení ihned – bez hledání v manuálech nebo kontaktování podpory.",
      },
      sweden: {
        badge: "Švédsko",
        title: "Vyvinuto ve Švédsku",
        body: "Švédský vývoj se švédským provozem serverů – navrženo pro vysokou bezpečnost, ochranu dat a spolehlivost provozu.",
      },
    },
    inverter: {
      badge: "Dostupné nyní",
      heading: "AI řízení pro *střídače* a *baterie* vašich zákazníků",
      body: "Nabídněte zákazníkům Solinteg a Sunwoda inteligentní energetický management jako doplněk. Získáte přehled portfolia a aftermarket – zákazník získá instalaci řízenou jako celek, automaticky.",
      note: "Dostupné pro Solinteg a Sunwoda.",
    },
    pilotBadge: "V pilotním testování · Brzy k dispozici",
    car: {
      heading: "Připojuje se *přímo* k autům zákazníků",
      body: "Intelligence se připojuje přímo k autům zákazníků a čte úroveň baterie a dojezd v reálném čase – takže nabíjení je vždy plánováno podle skutečné jízdy, ne podle odhadů.",
      note: "Podporuje většinu značek elektromobilů.",
      imgAlt: "Elektromobil připojený k Intelligence",
    },
    charging: {
      heading: "Nabíjí auta zákazníků, když je to *nejchytřejší*",
      body: "Intelligence plánuje nabíjení podle jízdy, solární výroby, baterie a ceny elektřiny – aby auto bylo připravené, když ho zákazník potřebuje, bez ručního řízení.",
      note: "Funguje se stávajícími nabíječkami zákazníků.",
      imgAlt: "Detail nabíjejícího elektromobilu",
    },
    heating: {
      heading: "Vytápí *domovy* zákazníků, když je to nejchytřejší",
      body: "Intelligence řídí tepelné čerpadlo jako součást celé instalace – počasí, solární výroba, baterie a cena elektřiny – aby komfort zůstal bez zbytečných nákladů.",
      note: "Funguje se stávajícími tepelnými čerpadly zákazníků.",
      imgAlt: "Tepelné čerpadlo řízené Intelligence",
    },
    faq: { label: "Často kladené otázky", heading: "Vše, co potřebujete *vědět*" },
    faqItems: [
      {
        q: "Co je Samvolt Intelligence pro instalatéry?",
        a: "Platforma, která vám pomáhá spravovat celé zákaznické portfolio po instalaci – monitorovat, optimalizovat a jednat dřív, než zákazník vůbec zjistí, že něco není v pořádku. Více času na nové zakázky, méně času na podporu.",
      },
      {
        q: "Jak to vytváří obchodní hodnotu pro nás?",
        a: "AI rozpozná instalace potřebující rozšíření – více soláru, baterie nebo nabíjení – a přináší přirozený doprodej. Zároveň proaktivní aftermarket snižuje servisní požadavky i odchod zákazníků, takže budujete opakující se vztahy místo jednorázových zakázek.",
      },
      {
        q: "Jak nám to pomáhá v aftermarketu?",
        a: "Vidíte stav všech instalací v reálném čase a zákazník je automaticky informován, když zařízení ztratí spojení nebo při výpadku proudu. Méně znepokojených hovorů a méně času na vysvětlování situace – spokojenější zákazníci a stabilnější provoz.",
      },
      {
        q: "Dokážete zvládat více instalací?",
        a: "Ano. Platforma je postavená pro portfolia – řídíte, porovnáváte a reportujete více instalací, regionů nebo zákazníků ze stejného pohledu, s rolemi a oprávněními.",
      },
      {
        q: "S jakým vybavením a systémy to funguje?",
        a: "Intelligence se integruje se střídači a bateriemi Solinteg a Sunwoda i s nabíjecí infrastrukturou. Můžete také napojit stávající energetické a obchodní systémy přes API.",
      },
      {
        q: "Jak je řešena bezpečnost a ochrana dat?",
        a: "Všechna data jsou zpracovávána na švédských serverech v souladu s evropskými zákony a předpisy včetně GDPR. Přihlášení probíhá přes BankID a platforma je vyvíjena ve Švédsku s důrazem na ochranu dat.",
      },
    ],
    cta: {
      heading: "Nechte své *portfolio* pracovat chytřeji",
      body: "Objednejte demo a podívejte se, jak Samvolt Intelligence optimalizuje každou instalaci a kilowatthodinu – automaticky, nonstop.",
    },
    footer: {
      tagline: "Získejte více ze své energie\u00A0– *plně automaticky*",
      developedInSweden: "Vyvinuto ve Švédsku",
      product: "Produkt",
      company: "Společnost",
      about: "O nás",
      support: "Podpora",
      rights: "© 2026 Samvolt Intelligence. Všechna práva vyhrazena.",
      privacy: "Zásady ochrany osobních údajů",
      terms: "Podmínky použití",
    },
  },
  zh: {
    nav: { app: "应用", compatibility: "兼容性", features: "功能", faq: "常见问题" },
    closeMenu: "关闭菜单",
    openMenu: "打开菜单",
    help: "帮助",
    myAccount: "我的账户",
    bookDemo: "预约演示",
    bookADemo: "预约演示",
    trialCta: "免费试用",
    hero: {
      h1: "*自动*优化能源\n覆盖您的整个安装*组合*。",
      sub: "面向安装商和销售团队的 AI 控制与售后服务——适用于 Solinteg 和 Sunwoda。",
      partners: "可通过以下合作伙伴获得：",
    },
    scrollReveal:
      "安装并不止于投运。Samvolt Intelligence 帮助安装商实现自然的增值销售——AI 识别需要扩容的电站——同时全天候优化整个客户组合。更少的支持工单、更满意的客户，以及实时的全面掌控。",
    setup: {
      label: "快速设置",
      heading: "*几分钟*即可就绪",
      body: "应用中三步——客户的电站即可完成连接。",
      steps: [
        {
          title: "扫描电费账单",
          body: "扫描客户的电费账单——AI 自动填写电站详情。",
        },
        {
          title: "获取地址",
          body: "通过您的位置自动获取客户地址和电价区域。",
        },
        {
          title: "连接并邀请",
          body: "添加设备并创建首位用户——然后一切就绪。",
        },
      ],
    },
    appViews: {
      heading: "所有视图集中在*一个*应用",
      body: "您的客户获得一款清晰易懂的应用，发电、电池、用电、功率和节省一目了然。",
    },
    bento: {
      dashboard: {
        title: "仪表盘",
        subtitle: "专为安装商打造",
        description:
          "简洁清晰的仪表盘，在一个视图中管理所有客户和电站。几秒内全面掌握——快速行动，轻松处理售后。",
      },
      notifications: {
        title: "自动通知",
        subtitle: "客户实时获悉",
        description:
          "停电或连接中断时的自动通知让客户随时知情。结果是更少的支持工单，以及更少花在无谓通话上的时间。",
      },
      ai: {
        title: "面向安装商的 AI",
        subtitle: "直接在浏览器中",
        description:
          "用 AI 更快排查故障。描述问题或输入错误代码，立即获得具体处理建议——无需翻阅手册或联系支持。",
      },
      sweden: {
        badge: "瑞典",
        title: "于瑞典开发",
        body: "瑞典开发，瑞典服务器运营——为高安全性、数据保护和运行可靠性而打造。",
      },
    },
    inverter: {
      badge: "现已可用",
      heading: "为客户的*逆变器*和*电池*提供 AI 控制",
      body: "向 Solinteg 和 Sunwoda 客户提供智能能源管理作为附加服务。您获得组合概览和售后能力——客户获得作为整体自动受控的电站。",
      note: "适用于 Solinteg 和 Sunwoda。",
    },
    pilotBadge: "试点测试中 · 即将推出",
    car: {
      heading: "*直接*连接客户的车辆",
      body: "Intelligence 直接连接客户车辆，实时读取电池电量和续航——充电始终按实际驾驶规划，而非猜测。",
      note: "支持大多数电动汽车品牌。",
      imgAlt: "已连接 Intelligence 的电动汽车",
    },
    charging: {
      heading: "在*最合适*的时候为客户车辆充电",
      body: "Intelligence 根据驾驶、光伏发电、电池和电价规划充电——车辆在客户需要时就绪，无需手动控制。",
      note: "兼容客户现有充电器。",
      imgAlt: "正在充电的电动汽车特写",
    },
    heating: {
      heading: "在最合适的时候为客户*住宅*供暖",
      body: "Intelligence 将热泵作为整个电站的一部分进行控制——天气、光伏发电、电池和电价——在无需多余成本的情况下保持舒适。",
      note: "兼容客户现有热泵。",
      imgAlt: "由 Intelligence 控制的热泵",
    },
    faq: { label: "常见问题", heading: "您需要*了解*的一切" },
    faqItems: [
      {
        q: "面向安装商的 Samvolt Intelligence 是什么？",
        a: "一个帮助您在安装后管理整个客户组合的平台——监控、优化，并在客户发现问题之前采取行动。把更多时间用于新业务，更少时间花在支持上。",
      },
      {
        q: "它如何为我们创造商业价值？",
        a: "AI 识别需要扩容的电站——更多光伏、电池或充电——带来自然的增值销售。同时，主动售后既减少支持工单，也降低客户流失，从而建立持续关系，而非一次性项目。",
      },
      {
        q: "它如何帮助我们做售后？",
        a: "您可实时查看所有电站状态，设备失联或停电时客户会自动收到通知。更少焦虑来电，更少解释时间——客户更满意，运营更稳定。",
      },
      {
        q: "能否同时管理多个电站？",
        a: "可以。平台为组合管理而建——在同一视图中控制、比较并报告多个电站、区域或客户，并支持角色与权限。",
      },
      {
        q: "支持哪些设备和系统？",
        a: "Intelligence 与 Solinteg 和 Sunwoda 逆变器及电池以及充电基础设施集成。您也可以通过 API 连接现有能源和业务系统。",
      },
      {
        q: "安全和数据保护如何处理？",
        a: "所有数据均在瑞典服务器上按照包括 GDPR 在内的欧洲法律法规处理。通过 BankID 登录，平台在瑞典开发并以数据保护为重点。",
      },
    ],
    cta: {
      heading: "让您的*组合*更聪明地运转",
      body: "预约演示，看看 Samvolt Intelligence 如何自动、全天候优化每一座电站和每一度电。",
    },
    footer: {
      tagline: "从能源中获得更多\u00A0– *完全自动*",
      developedInSweden: "于瑞典开发",
      product: "产品",
      company: "公司",
      about: "关于我们",
      support: "支持",
      rights: "© 2026 Samvolt Intelligence。保留所有权利。",
      privacy: "隐私政策",
      terms: "使用条款",
    },
  },
} satisfies Record<Locale, unknown>;

// TODO(business): swap these for the business mockup clips when ready.
// They currently reuse the consumer videos so the page renders.
const mockupVideos: { video: string; textSide: "left" | "right" }[] = [
  { video: "/home.webm", textSide: "left" },
  { video: "/scheme.webm", textSide: "right" },
  { video: "/savings.webm", textSide: "left" },
  { video: "/widget.webm", textSide: "right" },
];

// The three-tap setup flow from the app, distilled for the marketing page.
// Mirrors the onboarding: scan the bill, drop the address, connect devices +
// invite the first user.
const setupIcons: LucideIcon[] = [ScanLine, MapPin, PlugZap];

const inverterBrands = ["Solinteg", "Sunwoda"];

const carBrands = [
  "Tesla",
  "Volvo",
  "Volkswagen",
  "Polestar",
  "Kia",
  "Audi",
  "Mercedes",
  "Hyundai",
  "BYD",
  "Peugeot",
  "Citroën",
  "Dacia",
  "Fiat",
  "Honda",
  "Jaguar",
  "Jeep",
  "Land Rover",
  "Lexus",
  "Mazda",
  "Nissan",
  "Opel",
  "Porsche",
  "Renault",
  "Rivian",
  "Subaru",
  "Toyota",
  "XPENG",
];

const evChargers = [
  "Easee",
  "Zaptec",
  "Charge Amps",
  "Garo",
  "Wallbox",
  "go-e",
  "KEBA",
  "Heidelberg",
  "myenergi",
  "Schneider Electric",
  "Tesla",
];

const heatPumps = [
  "NIBE",
  "Bosch",
  "Daikin",
  "Mitsubishi",
  "Panasonic",
  "Toshiba",
  "Fujitsu",
  "ADAX",
  "Ecobee",
  "Mill",
  "Sensibo",
  "Tado",
  "Honeywell TCC",
  "Resideo",
  "Function",
];

export default function ForetagPage({ locale = "sv" }: { locale?: Locale }) {
  const t = COPY[locale];
  // Serif accent used inside dark-on-light section headings.
  const darkAccent = "font-newsreader font-normal italic text-neutral-900/60";

  const faqItems: FaqItem[] = t.faqItems;

  // Logos for the carousel strip
  const partners = [
    { name: "Ahlsell", src: "/partners/ahlsell.svg" },
    { name: "Rexel", src: "/partners/rexel.svg" },
    { name: "KP", src: "/partners/kp.webp" },
    { name: "Horizon", src: "/partners/horizon-logo.png" },
    { name: "Energy Group", src: "/partners/energygroup-logo.webp" },
  ];

  // Primary nav links – shown inline on desktop, inside the hamburger on mobile.
  // Each points at a real in-page section via its anchor ID.
  const navLinks = [
    { label: t.nav.app, href: "#app" },
    { label: t.nav.compatibility, href: "#kompabilitet" },
    { label: t.nav.features, href: "#funktioner" },
    { label: t.nav.faq, href: "#faq" },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const router = useRouter();

  // This is the business view. The private (consumer) view lives at /.
  const persona: Persona = "business";

  // Remember the persona choice, then route to the matching view.
  const choosePersona = (next: Persona) => {
    try {
      const prev = JSON.parse(localStorage.getItem(ONBOARDING_KEY) || "{}");
      localStorage.setItem(
        ONBOARDING_KEY,
        JSON.stringify({ ...prev, persona: next }),
      );
    } catch {
      /* ignore unwritable storage */
    }
    router.push(localePath(locale, next === "business" ? "/foretag" : "/"));
  };

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <main className="relative w-full bg-black font-sans text-white">
      {/* First-visit onboarding: policy consent + persona choice */}
      <WelcomeDialog locale={locale} onComplete={choosePersona} />

      {/* ============================================================
          MOBILE MENU – full-screen overlay (hamburger target)
          ============================================================ */}
      {/* Dimmed backdrop */}
      <div
        onClick={() => setMenuOpen(false)}
        className={`fixed inset-0 z-[55] bg-black/40 transition-opacity duration-300 ease-out md:hidden ${
          menuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden
      />
      {/* iOS-style frosted sheet, slides up from the bottom */}
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed inset-x-0 bottom-0 z-[60] flex max-h-[92svh] flex-col rounded-t-[28px] border-t border-white/10 bg-neutral-950/80 pb-[max(env(safe-area-inset-bottom),1.25rem)] shadow-[0_-20px_60px_-12px_rgba(0,0,0,0.6)] backdrop-blur-2xl transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden ${
          menuOpen ? "translate-y-0" : "pointer-events-none translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 pb-5 pt-6">
          <img
            src="/brand/intelligence-l.svg"
            alt="Samvolt Intelligence"
            className="h-7 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label={t.closeMenu}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
          >
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        <div className="flex flex-col gap-6 overflow-y-auto px-5 pb-2">
          {/* Primary navigation – iOS grouped list */}
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] divide-y divide-white/10">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between px-5 py-4 text-[1.0625rem] font-medium text-white transition-colors active:bg-white/10"
              >
                {link.label}
                <ChevronRight size={18} strokeWidth={2} className="text-white/30" />
              </a>
            ))}
          </div>

          {/* Secondary links – iOS grouped list */}
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] divide-y divide-white/10">
            <a
              href={localePath(locale, "/support")}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-5 py-4 text-[1.0625rem] text-white/85 transition-colors active:bg-white/10"
            >
              {t.help}
              <ChevronRight size={18} strokeWidth={2} className="text-white/30" />
            </a>
            <a
              href={APP_LOGIN_URL}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-5 py-4 text-[1.0625rem] text-white/85 transition-colors active:bg-white/10"
            >
              {t.myAccount}
              <ArrowUpRight size={18} strokeWidth={2} className="text-white/30" />
            </a>
            <LanguageSwitcher locale={locale} path="/foretag" variant="menu" />
          </div>
        </div>

        <div className="px-5 pt-5">
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className="flex w-full items-center justify-center rounded-[14px] bg-white px-6 py-4 text-[1rem] font-semibold text-black transition-colors hover:bg-white/90 active:bg-white/80"
          >
            {t.bookDemo}
          </a>
        </div>
      </div>

      {/* First viewport: header + hero fill the screen exactly so the
          white frame around the hero is even on every side */}
      <div className="flex h-svh flex-col">
        {/* ============================================================
          TOP UTILITY BAR – slim nav above the main header
          ============================================================ */}
        <div className="relative z-50 flex h-8 items-center justify-between px-5 md:px-7">
          <PersonaToggle active={persona} onSelect={choosePersona} locale={locale} />
          <div className="flex items-center gap-3.5 sm:gap-6">
            <a
              href={localePath(locale, "/support")}
              className="text-[0.75rem] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.help}
            </a>
            <a
              href={APP_LOGIN_URL}
              className="inline-flex items-center gap-1 text-[0.75rem] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.myAccount}
              <ArrowUpRight size={10} strokeWidth={2} />
            </a>
            <LanguageSwitcher locale={locale} path="/foretag" />
          </div>
        </div>
        {/* ============================================================
          HERO – inset, rounded card with white padding around it
          ============================================================ */}
        <div className="min-h-0 flex-1 p-1.5 md:p-2 pt-0 md:pt-0">
          <section className="relative h-full min-h-[28rem] w-full overflow-hidden rounded-[16px] md:rounded-[22px]">
            {/* Background video */}
            <video
              className="absolute brightness-120 inset-0 h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source
                src="/hero-videos/hero_video_av1.webm"
                type='video/webm; codecs="av01.0.05M.08"'
              />
              <source src="/hero-videos/hero_video_mp4.mp4" type="video/mp4" />
            </video>

            {/* Subtle darkening for legibility */}

            {/* Content */}
            <div className="relative z-10 flex h-full flex-col">
              {/* Main nav – inside the hero card, over the video */}
              <header className="relative flex items-center justify-between px-5 pt-5 md:px-7 md:pt-6">
                {/* Logo */}
                <a href={localePath(locale, "/foretag")} className="inline-flex items-center">
                  <img
                    src="/brand/intelligence-l.svg"
                    alt="Samvolt Intelligence"
                    className="h-7 w-auto md:h-9"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                </a>

                {/* Right nav – inline on desktop */}
                <nav className="hidden items-center gap-8 text-[0.875rem] md:flex">
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="text-white/70 transition-colors hover:text-white"
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>

                {/* Hamburger – mobile only */}
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  aria-label={t.openMenu}
                  className="-mr-1 flex items-center justify-center p-1 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] transition-opacity hover:opacity-80 md:hidden"
                >
                  <Menu size={28} strokeWidth={2} />
                </button>
              </header>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Hero bottom row */}
              <div className="flex items-end justify-between gap-6 px-5 pb-7 md:px-7 md:pb-8">
                <div>
                  <h1 className="max-w-3xl text-balance font-display text-[2.75rem] font-normal leading-[1.02] tracking-[-0.035em] text-white md:text-[3.25rem] lg:max-w-4xl lg:text-[3.5rem] 2xl:max-w-6xl 2xl:text-[4.25rem]">
                    {renderAccented(t.hero.h1)}
                  </h1>
                  <p className="mt-4 max-w-xl text-[0.9375rem] leading-relaxed text-white/75 md:text-[1rem]">
                    {t.hero.sub}
                  </p>

                  <a
                    href={BOOK_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => capture("business_hero_demo_clicked")}
                    className="group mt-8 inline-flex items-center rounded-[4px] border border-white/15 bg-white/10 px-5 py-2.5 text-[0.875rem] font-medium text-white backdrop-blur-md transition-colors hover:bg-white/20"
                  >
                    {t.bookDemo}
                    <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
                      <span className="overflow-hidden">
                        <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-white/25 text-white">
                          <ArrowRight size={13} strokeWidth={2} />
                        </span>
                      </span>
                    </span>
                  </a>
                </div>
              </div>

              {/* Logo carousel strip */}
              <section className="px-5 pb-6 md:px-7 md:pb-7">
                <p className="text-[0.75rem] text-white/45">{t.hero.partners}</p>
                <div
                  className="relative mt-3 flex items-center overflow-hidden border-t border-white/10 pt-5"
                  style={{
                    WebkitMaskImage:
                      "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                    maskImage:
                      "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                  }}
                >
                  <div className="flex w-max animate-logo-scroll items-center">
                    {[...Array(6)]
                      .flatMap(() => partners)
                      .map((logo, index) => (
                        <div
                          key={index}
                          className="flex shrink-0 items-center justify-center px-7 sm:px-10"
                        >
                          <img
                            src={logo.src}
                            alt={logo.name}
                            className="h-4 w-auto max-w-none object-contain opacity-50 transition-opacity duration-300 hover:opacity-90 sm:h-5"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </section>
            </div>
          </section>
        </div>
      </div>

      {/* ============================================================
          SCROLL-REVEAL INTRO + APP MOCKUP
          Shared white surface so the black panel's shadow can bleed
          continuously into the mockup section instead of being cut.
          ============================================================ */}
      <div className="bg-white">
      <section className="pb-24">
        <div className="rounded-b-[28px] bg-black px-5 py-32 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.55)] md:px-7 md:py-48">
          <div className="mx-auto max-w-4xl">
            <ScrollRevealText
              className="font-sans text-[1.5rem] font-normal leading-[1.35] tracking-[-0.02em] sm:text-[1.875rem] md:text-[2.75rem] md:leading-[1.3] pb-8"
              text={t.scrollReveal}
            />
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 text-[1.25rem] font-medium text-white/40 transition-colors duration-300 ease-out hover:text-white md:text-[1.4375rem]"
            >
              <span className="pb-1 decoration-current underline-offset-4">{t.bookADemo}</span>
              <ArrowRight
                size={18}
                strokeWidth={2}
                className="transition-transform duration-300 ease-out group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================
          QUICK SETUP – 3 steps (mirrors the app onboarding)
          ============================================================ */}
      <section className="px-5 pb-24 md:px-7 md:pb-32">
        <div className="mx-auto max-w-[1200px]">
          <div className="max-w-2xl">
            <p className="text-[0.875rem] tracking-tight text-black/40">
              {t.setup.label}
            </p>
            <h2 className="mt-4 text-balance font-display text-[2rem] font-normal leading-[1.08] tracking-[-0.03em] text-black sm:text-[2.5rem] md:text-[3rem]">
              {renderAccented(t.setup.heading, "font-newsreader font-normal italic text-black/70")}
            </h2>
            <p className="mt-4 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
              {t.setup.body}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-3 md:mt-16 md:grid-cols-3">
            {t.setup.steps.map((step, i) => {
              const Icon = setupIcons[i];
              return (
                <div
                  key={step.title}
                  className="flex flex-col gap-6 rounded-[8px] border border-neutral-900/10 bg-neutral-100 p-7 md:p-8"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-white text-neutral-900 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                      <Icon size={20} strokeWidth={1.75} />
                    </span>
                    <span className="font-newsreader text-[1.75rem] italic leading-none text-neutral-300">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-[1.125rem] font-medium tracking-tight text-neutral-900">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[0.9375rem] leading-relaxed text-neutral-600">
                      {step.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          BENTO WORK / FEATURE GRID
          ============================================================ */}
      <section id="funktioner" className="scroll-mt-16 bg-white px-3 py-3 md:px-4 md:py-4 pb-0 md:pb-0">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* --- Card: Dashboard för installatörer --- */}
          <VideoBentoCard
            video="/890_1080x60_shots_so.mp4"
            icon={<LayoutDashboard size={18} strokeWidth={1.75} />}
            title={t.bento.dashboard.title}
            subtitle={t.bento.dashboard.subtitle}
            description={t.bento.dashboard.description}
          />

          {/* --- Card: Automatiska aviseringar --- */}
          <VideoBentoCard
            video="/notifcationvideo.mp4"
            contain
            icon={<BellRing size={18} strokeWidth={1.75} />}
            title={t.bento.notifications.title}
            subtitle={t.bento.notifications.subtitle}
            description={t.bento.notifications.description}
          />

          {/* --- Card: Effekt & marknad --- */}
          <VideoBentoCard
            video="/ai_chat.mp4"
            contain
            icon={<TrendingUp size={18} strokeWidth={1.75} />}
            title={t.bento.ai.title}
            subtitle={t.bento.ai.subtitle}
            description={t.bento.ai.description}
          />

          {/* --- Card: Effekttoppar (video, loops continuously) --- */}
          <div
            className="group relative flex h-[48svh] flex-col overflow-hidden rounded-[8px] bg-black transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[62svh] sm:max-h-[610px]"
          >
            <video
              src="/4-5%20(online-video-cutter.com).mp4"
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-hidden
            />
          </div>

          {/* --- Card: Stödtjänster (dark neutral, globe, wide, badge) --- */}
          <div
            className="group relative flex h-[48svh] flex-col justify-start overflow-hidden rounded-[8px] bg-gradient-to-br from-[#262626] to-[#161616] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:col-span-2 sm:h-[62svh] sm:max-h-[610px]"
          >
            {/* Globe rising from the bottom */}
            <BrandGlobe sizeFactor={1.6} offsetY="58%" />
            {/* Gradient overlay – darker at the top where the text sits */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-transparent" />

            <div className="relative z-10 mx-auto max-w-md pt-4 text-center">
              <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[0.75rem] font-medium tracking-tight text-white/85 backdrop-blur-sm">
                <Flag locale="sv" className="h-3" />
                {t.bento.sweden.badge}
              </span>
              <h3 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.02em] text-white md:text-[2.25rem]">
                {t.bento.sweden.title}
              </h3>
              <p className="mt-3 text-[0.875rem] leading-relaxed text-white/60 md:text-[1rem]">
                {t.bento.sweden.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          APP VIEWS – every customer app view at a glance
          ============================================================ */}
      <section id="app" className="scroll-mt-16 bg-white px-3 py-16 md:px-4 md:py-24">
        <div className="mx-auto max-w-[1600px]">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
              {renderAccented(t.appViews.heading, darkAccent)}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
              {t.appViews.body}
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 justify-items-center gap-8 sm:grid-cols-3 md:mt-16 md:gap-10">
            {mockupVideos.slice(0, 3).map(({ video }) => (
              <div key={video} className="flex items-center justify-center">
                <video
                  className="h-auto w-full max-w-[340px] object-contain"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden
                >
                  <source src={`${video.replace(/\.webm$/, ".mp4")}?v=3`} type="video/mp4" />
                </video>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>

      {/* ============================================================
          INVERTER / BATTERY – primary compatibility (live today)
          ============================================================ */}
      <section id="kompabilitet" className="scroll-mt-16 bg-white px-3 pt-3 md:px-4 md:pt-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative order-1 min-h-[18rem] overflow-hidden bg-neutral-900 lg:order-none lg:min-h-[36rem]">
            <video
              className="absolute inset-0 h-full w-full object-cover opacity-80"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden
            >
              <source src="/864_1080x60_shots_so.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
          </div>

          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t.inverter.badge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.inverter.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.inverter.body}
              </p>

              <BrandList brands={inverterBrands} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ELECTRIC CAR – split feature (image left, text right)
          ============================================================ */}
      <section className="bg-white px-3 pt-3 md:px-4 md:pt-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="/326740_1_5.jpg"
              alt={t.car.imgAlt}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>

          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.car.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.car.body}
              </p>

              {/* Supported car brands */}
              <BrandList brands={carBrands} note={t.car.note} locale={locale} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          EV CHARGING – split feature (text panel + image)
          ============================================================ */}
      <section className="bg-white px-3 py-3 md:px-4 md:py-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[1.1fr_0.9fr]">
          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.charging.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.charging.body}
              </p>

              {/* Supported EV chargers */}
              <BrandList brands={evChargers} note={t.charging.note} locale={locale} />
            </div>
          </div>

          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="/ev-charger.jpg"
              alt={t.charging.imgAlt}
              className="absolute inset-0 h-full w-full object-cover object-right transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
          HEAT PUMP – split feature (image left, text right)
          ============================================================ */}
      <section className="bg-white px-3 pb-0 md:px-4 md:pb-0">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="pexels-aibek-skakov-418917601-29398678.jpg"
              alt={t.heating.imgAlt}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>

          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.heating.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.heating.body}
              </p>

              {/* Supported heat pump / HVAC brands */}
              <BrandList brands={heatPumps} note={t.heating.note} locale={locale} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TIMELINE
          ============================================================ */}
      <div className="bg-gradient-to-b from-white via-[#f6f8f8] to-frost text-pine">
        <Timeline locale={locale} />
      </div>

      {/* ============================================================
          FAQ
          ============================================================ */}
      <section id="faq" className="scroll-mt-16 bg-white px-5 py-24 md:px-7 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[0.875rem] tracking-tight text-black/40">{t.faq.label}</p>
          <h2 className="mx-auto mt-4 max-w-2xl text-balance font-display text-[2rem] font-normal leading-[1.08] tracking-[-0.03em] text-black sm:text-[2.5rem] md:text-[3rem]">
            {renderAccented(t.faq.heading, "font-newsreader font-normal italic text-black/70")}
          </h2>
        </div>
        <FaqAccordion items={faqItems} />
      </section>

      {/* ============================================================
          CTA + FOOTER
          ============================================================ */}
      <footer className="bg-black px-3 pb-8 pt-0 md:px-4 md:pb-10">
        {/* ── CTA ── full-bleed & seamless at all sizes */}
        <section className="relative -mx-3 overflow-hidden bg-gradient-to-b from-[#161616] to-black px-6 py-24 md:-mx-4 md:px-10 md:py-36">
          {/* Soft top glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[min(820px,90%)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.16),transparent)]"
          />

          <div className="relative z-10 mx-auto max-w-3xl text-center">
            <h2 className="mx-auto max-w-3xl text-balance font-display text-[2.25rem] font-medium leading-[1.04] tracking-[-0.035em] text-white sm:text-[2.75rem] md:text-[3.5rem]">
              {renderAccented(t.cta.heading)}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[1.0625rem] leading-relaxed text-white/55">
              {t.cta.body}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => capture("business_footer_demo_clicked")}
                className="group inline-flex w-full items-center justify-center rounded-[6px] bg-white px-6 py-3.5 text-[0.9375rem] font-medium text-black transition-colors hover:bg-white/90 sm:w-auto"
              >
                {t.bookDemo}
                <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
                  <span className="overflow-hidden">
                    <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-black/25 text-black">
                      <ArrowRight size={13} strokeWidth={2} />
                    </span>
                  </span>
                </span>
              </a>
              <a
                href={APP_TRIAL_URL}
                onClick={() => capture("business_footer_trial_clicked")}
                className="inline-flex w-full items-center justify-center rounded-[6px] border border-white/15 bg-white/[0.04] px-6 py-3.5 text-[0.9375rem] font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/10 sm:w-auto"
              >
                {t.trialCta}
              </a>
            </div>
          </div>
        </section>

        {/* ── Footer body ── */}
        <div className="mx-auto max-w-7xl px-2 md:px-3">
          <div className="mt-20 flex flex-col gap-12 md:mt-24 md:flex-row md:items-start md:justify-between md:gap-8">
            <div className="max-w-md">
              <p className="text-balance font-sans text-[1.75rem] font-normal leading-[1.12] tracking-[-0.03em] text-white sm:text-[2.125rem]">
                {renderAccented(
                  t.footer.tagline,
                  "whitespace-nowrap font-newsreader font-normal italic text-white/55",
                )}
              </p>
              <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[0.8125rem] text-white/55">
                <Flag locale="sv" className="h-3.5" />
                {t.footer.developedInSweden}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-10 sm:gap-16">
              <div className="flex flex-col gap-3.5">
                <span className="text-[0.75rem] uppercase tracking-wider text-white/35">{t.footer.product}</span>
                <a href="#app" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.app}
                </a>
                <a href="#kompabilitet" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.compatibility}
                </a>
                <a href="#funktioner" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.features}
                </a>
                <a href="#faq" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.faq}
                </a>
              </div>
              <div className="flex flex-col gap-3.5">
                <span className="text-[0.75rem] uppercase tracking-wider text-white/35">{t.footer.company}</span>
                <a href={localePath(locale, "/company")} className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.footer.about}
                </a>
                <a href={localePath(locale, "/support")} className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.footer.support}
                </a>
              </div>
            </div>
          </div>

          {/* Bottom legal bar */}
          <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-[0.75rem] text-white/35 sm:flex-row sm:items-center sm:justify-between">
            <span>{t.footer.rights}</span>
            <div className="flex gap-6">
              {/* Legal documents exist in Swedish only */}
              <a href="/integritetspolicy" className="transition-colors hover:text-white/70">
                {t.footer.privacy}
              </a>
              <a href="/anvandarvillkor" className="transition-colors hover:text-white/70">
                {t.footer.terms}
              </a>
              <CookieSettingsLink locale={locale} />
            </div>
          </div>

          {/* Oversized wordmark flourish – edges fade so it reads as designed */}
          <img
            src="/brand/intelligence-l.svg"
            alt=""
            aria-hidden
            className="pointer-events-none mt-12 w-full select-none opacity-[0.45] invert [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)] md:mt-16"
          />
        </div>
      </footer>
    </main>
  );
}
